import { heroActionTimeline, heroActionSourceFrameAt } from '@/modules/game-domain/hero-action-timeline';
import type { HeroAnimationView, HeroAnimationFacing } from '@/modules/game-domain/render-snapshot';
export type { HeroAnimationView } from '@/modules/game-domain/render-snapshot';
import { worldToScreen } from './coordinates';
import { TILE_WIDTH, TILE_HEIGHT } from '@/shared/constants';
import { meshyHeroClip, meshyClipDuration, meshyClipFrame } from './meshy-hero-clips';
import { HeroIdleScheduler } from './hero-idle-scheduler';

export const HERO_ANIMATION = {
  modelVariant: 0, gaitSteps: 8, directions: ['east', 'south-east', 'south', 'south-west', 'west', 'north-west', 'north', 'north-east'] as const,
  displayHeight: 90,
  strideDistancePx: 95,

};
/** Undo vertical foreshortening so equal ground travel has equal gait in every direction. */
export function walkGroundDistance(dx: number, dy: number): number {
  return Math.hypot(dx, dy * TILE_WIDTH / TILE_HEIGHT);
}
export function screenFacing(dx: number, dy: number, fallback: HeroAnimationFacing = 'south'): HeroAnimationFacing {
  if (Math.hypot(dx, dy) < 0.001) return fallback;
  // Atlas headings are evenly spaced on the ground, whose screen Y is foreshortened.
  const angle = Math.atan2(dy * TILE_WIDTH / TILE_HEIGHT, dx);
  const previousAngle = HERO_ANIMATION.directions.indexOf(fallback) * Math.PI / 4;
  const distance = Math.abs(Math.atan2(Math.sin(angle - previousAngle), Math.cos(angle - previousAngle)));
  // Five degrees of overlap avoids direction flicker at each 22.5-degree ground boundary.
  if (distance <= Math.PI / 8 + Math.PI / 36) return fallback;
  return HERO_ANIMATION.directions[(Math.round(angle / (Math.PI / 4)) + 8) % 8]!;
}

/** Presentation only: distance-driven gait, sampled by the engine's sole RAF. */
export class HeroAnimationController {
  private idle: HeroIdleScheduler;
  constructor(random: () => number = Math.random) {
    this.idle = new HeroIdleScheduler((variant, clip, facing) => {
      const definition = meshyHeroClip(variant, clip, facing);
      return definition ? meshyClipDuration(definition) : undefined;
    }, random);
  }
  private previous: { x: number; y: number } | null = null;
  private facing: HeroAnimationFacing = 'south';
  private walkDistance = 0;
  private action: { clip: 'attack' | 'block' | 'dodge'; startMs: number } | null = null;

  reset(): void {
    this.previous = null; this.facing = 'south';
    this.walkDistance = 0; this.action = null;
    this.idle.reset();
  }
  attack(nowMs: number): void { this.action = { clip: 'attack', startMs: nowMs }; }
  defend(clip: 'block' | 'dodge', nowMs: number): void { this.action = { clip, startMs: nowMs }; }
  sample(nowMs: number, x: number, y: number, enemy: { x: number; y: number } | null, dead = false, modelVariant?: number, idleAllowed = true, stopFacing?: {x:number;y:number}): HeroAnimationView {
    const screen = worldToScreen(x, y);
    const dx = this.previous ? screen.x - this.previous.x : 0;
    const dy = this.previous ? screen.y - this.previous.y : 0;
    this.previous = screen;
    const distance = walkGroundDistance(dx, dy), moving = !dead && distance > 0.001;
    if (moving || enemy || dead || this.action || !idleAllowed) this.idle.reset();
    if (dead) this.action = null;
    if (enemy && !dead) {
      const target = worldToScreen(enemy.x, enemy.y);
      this.facing = screenFacing(target.x - screen.x, target.y - screen.y, this.facing);
    } else if (moving) this.facing = screenFacing(dx, dy, this.facing);
    else if (!dead && !this.action && stopFacing && [stopFacing.x,stopFacing.y].every(Number.isFinite)) {
      const direction=worldToScreen(stopFacing.x,stopFacing.y);
      this.facing=screenFacing(direction.x,direction.y,this.facing);
    }
    if (this.action) {
      const elapsed = Math.max(0, nowMs - this.action.startMs);
      const timeline = heroActionTimeline(this.action.clip, this.facing);
      const candidate = modelVariant === undefined ? undefined : meshyHeroClip(modelVariant, this.action.clip, this.facing);
      // Server attack cadence owns the action window; FBX holds only distribute poses inside it.
      const duration = candidate ? heroActionTimeline(this.action.clip, 'east').durationMs : timeline.durationMs;
      if (elapsed < duration) {
        this.walkDistance = 0;
        const progress = elapsed / duration;
        return { clip: this.action.clip, facing: this.facing,
          frame: candidate ? meshyClipFrame(candidate, progress) : heroActionSourceFrameAt(this.action.clip, elapsed, this.facing), progress };
      }
      this.action = null;
    }
    if (moving) {
      // No time-based cap/backlog: the displayed phase follows this displayed displacement.
      const cycles = (this.walkDistance + distance) / HERO_ANIMATION.strideDistancePx;
      const progress = Math.max(0, cycles - Math.floor(cycles + 1e-9));
      this.walkDistance = progress * HERO_ANIMATION.strideDistancePx;
      return { clip: 'walk', facing: this.facing, frame: Math.floor(progress * HERO_ANIMATION.gaitSteps), progress };
    }
    this.walkDistance = 0;
    if (!enemy && !dead && idleAllowed && modelVariant !== undefined) {
      const idle = this.idle.sample(nowMs, modelVariant, this.facing);
      const definition = idle ? meshyHeroClip(modelVariant, idle.clip, this.facing) : undefined;
      if (idle && definition) return { ...idle, facing: this.facing, frame: meshyClipFrame(definition, idle.progress) };
    }
    const clip = enemy && !dead ? 'combat-idle' : 'idle';
    if (clip === 'combat-idle') return { clip, facing: this.facing, frame: 0, progress: 0 };
    const candidate = modelVariant === undefined || dead || (!idleAllowed && clip === 'idle') ? undefined : meshyHeroClip(modelVariant, clip, this.facing);
    if (candidate) {
      const duration = meshyClipDuration(candidate);
      const progress = duration > 0 ? (Math.max(0, nowMs) % duration) / duration : 0;
      return { clip, facing: this.facing, frame: meshyClipFrame(candidate, progress), progress };
    }
    return { clip, facing: this.facing, frame: 0 };
  }
}
